import type * as React from 'react';

import { Tooltip } from '@/components/Atoms/Tooltip/Tooltip';
import { IconButton } from '@/components/IconButton/IconButton';
import type { ClickHandlerArgs } from '@/components/SourceCards/hooks/useFeedCard';
import { useDetectChannelId } from '@/features/Channels/hooks/useDetectChannelId';
import { CommentActionIcon } from '@/features/Comment/components/CommentActionIcon';
import {
  useCanAccessComments,
  usePrefetchComment,
} from '@/features/Comment/hooks';
import { useCommentsClickHandler } from '@/features/Comment/hooks/useCommentsClickHandler';
import { useFetchCommentCount } from '@/features/Comment/hooks/useFetchCommentCount';

import { CommentButtonDisabled } from './CommentButtonDisabled';

const CommentActionButtonImpl = ({
  cardAction,
  deeplink,
  label,
  disabled = false,
  overrideClickHandler,
  ...rest
}: CommentActionButtonType) => {
  const { channelId } = useDetectChannelId();
  const { data } = useFetchCommentCount({
    channelId,
    deeplink,
    enabled: !disabled,
  });

  const clickHandler = useCommentsClickHandler({
    overrideClickHandler,
    cardAction,
    onClick: rest.onClick,
  });

  usePrefetchComment();

  const _label = label || 'Comments';

  return disabled ? (
    <CommentButtonDisabled />
  ) : (
    <Tooltip label={_label}>
      <IconButton {...rest} aria-label={_label} onClick={clickHandler}>
        <CommentActionIcon
          count={data ? data.count : undefined}
          fill={data ? !!data.count : undefined}
        />
      </IconButton>
    </Tooltip>
  );
};

export const CommentActionButton = (props: CommentActionButtonType) => {
  const showComments = useCanAccessComments();

  if (!showComments) {
    return null;
  }

  return <CommentActionButtonImpl {...props} />;
};

export type CommentActionButtonType =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    overrideClickHandler?: ({ comments }: ClickHandlerArgs) => void;
    cardAction?: boolean;
    deeplink: string;
    count?: number;
    label?: string;
  };
